// Wire contracts for the Hedging Grid CoPilot (CR-20260619-001).
//
// These mirror the FROZEN M1 backend protobuf DTOs (ets.platform: ETS.ShellService.Shell.Protocol
// /Operations|Dto/HedgingGridCoPilot). Field names are camelCase — the shell-router COMMAND path
// serializes with plain JSON.stringify (NO class-transformer; verified) and the backend Newtonsoft
// deserializes case-insensitively into the PascalCase C# DTOs. The inbound ESS push arrives
// camelCase on the FE (mirrors CollarsPositionChangedDto). Do NOT add class-transformer decorators.
//
// Send: RunCoPilotSkill (command) / CancelCoPilotRun (command). Receive: CoPilotSolutionsReadyDto
// over ESS, correlated by runId.

import { ProtocolCommand } from '../../service-model/protocol-command.interface';
import { SHELL_PROTOCOL_OPERATIONS_NS } from '../../shell-communication/operations/shell-protocol-namespace.const';

// Wire version of the RunCoPilotSkill contract (backend RunCoPilotSkill.ContractVersion). Bump only
// in lockstep with a backend contract change.
// v2 (2026-06-22): added minProfitNormalized (Select/Adjust) + maxRisk.normalized (dollar) — the
// "Normalized" discriminator. Backend bumped CoPilotContextBundleAssembler.CurrentSchemaVersion 1->2.
// v3 (2026-08-13): the "Normalized" discriminator is GONE and so is defaultQuantity. minProfit was
// renamed minProfitDollars and both figures cross the wire as PLAIN DOLLARS, converted panel-side.
// The three returned measures moved to *Dollars on new proto numbers (14/15/16). This is a BREAKING
// change in both directions, which is why the version moves rather than the fields being reused.
// v4 (2026-08-20, CR-20260820-002): ADDITIVE. priceRange.kind gains "expectedRange" (the Range form
// with both edges measured from the option-implied expected move instead of typed), and
// adjust.expectedRangeModel carries the Expected Move model selection behind it. A client that never
// sends either is byte-identical to v3.
export const COPILOT_CONTRACT_VERSION = 4;

// The ESS message type the FE subscribes to (= backend class name; MessageGuid d11f7a5f-... server-side).
export const COPILOT_SOLUTIONS_READY_MESSAGE_TYPE = 'CoPilotSolutionsReadyDto';

// ---- Request-side DTOs (FE -> BE, on RunCoPilotSkill) ---------------------------------

// Full working-state position record (ADR-031 — FE-supplied; CoPilot-OWNED, deliberately NOT the
// ETS-CORE PositionDto). The facade (M3) projects HedgePositionModel into this shape.
export interface CoPilotPositionDto {
    ticker: string;
    underlying: string;
    asset: string;
    quantity: number;             // signed net (long > 0, short < 0)
    strike: number | null;        // null for the underlying
    expiry: string | null;        // null/empty for the underlying
    optionType: string | null;    // "Call" | "Put" | null
    avgPrice: number | null;
    label: string;
    groupId: string;
    // The owning hedge's colour, projected by the Matrix facade read so the read-only Transactions
    // view can colour each hedge's before/transaction/after block.
    //
    // ⚠ NO LONGER FE-ONLY. This used to say the backend never read it and simply ignored the unknown
    // JSON field, which was true of the Adjust skill and is no longer true of the Close: the close
    // delivery carries a hedge colour on every leg (contract H1 section 5) and CoPilotPositionDto
    // gained a Color member to receive this one (ruling L16). The Adjust path still ignores it.
    color?: string | null;
}

export interface CoPilotPriceRangeDto {
    // "fillGap" added by CR-20260724-001 (additive — the three original values are untouched, and the
    // backend field already accepts it). Fill-Gap carries the same two strikes as "bounded".
    //
    // "expectedRange" added by CR-20260820-002 (additive). It is "bounded" with the two edges
    // measured by the backend from the option-implied expected move, so startStrike/endStrike travel
    // NULL and the backend fills them in before the engine sees the run.
    //
    // ⚠ A DISTINCT KIND RATHER THAN A BOOLEAN BESIDE THE STRIKES, DELIBERATELY. This path is plain
    // JSON and the backend ignores members it does not know, so a flag sent to a shell that predates
    // it would simply vanish and the run would proceed on the manual shape with two null strikes. An
    // unrecognised KIND is refused by name instead, which is what a trader on a half-deployed
    // environment needs to see.
    kind: string;                 // "bounded" | "fillGap" | "followThrough" | "wall" | "expectedRange"
    startStrike: number | null;
    endStrike: number | null;
    // ⚠ LEGACY SINCE CR-20260824-015 — the two-sided Follow-Through carries its sides below and
    // the backend reads this on NO form. It travels null; a backend that predates the rewrite
    // refuses the sides-without-direction shape by name, which is the loud half of compatibility.
    direction: string | null;     // legacy "up" | "down"
    // THE TWO-SIDED FOLLOW-THROUGH (CR-20260824-015, additive; "followThrough" only, null on
    // every other form). Each side carries its OWN minimum in PLAIN DOLLARS — converted panel-side
    // exactly as the run-level figure is — and its OWN Harvest command. closeRemainingPositions is
    // ONE toggle for both sides: remaining = every node no enabled side claims; false = leave that
    // region exactly as the book has it (the default), true = sell it out to flat zero.
    followThroughUp?: CoPilotFollowThroughSideDto | null;
    followThroughDown?: CoPilotFollowThroughSideDto | null;
    closeRemainingPositions?: boolean;
}

// One side of the two-sided Follow-Through (CR-20260824-015). Mirrors the backend
// CoPilotFollowThroughSide field for field.
export interface CoPilotFollowThroughSideDto {
    enabled: boolean;
    startStrike: number | null;
    minProfitDollars: number;     // PLAIN DOLLARS, converted before send like every money figure
    harvest: boolean;
}

// CR-20260820-002. The Expected Move RANGE MODEL, couriered for an "expectedRange" run.
//
// ⚠ THE MODEL, NEVER THE BAND. The panel can compute the band itself — that is exactly the trap.
// Resolving the expected range frontend-side is prohibited (owner ruling CR-20260715-001, restated
// in ADR-035's rejected alternatives, governed by ADR-038): the frontend says WHICH MODEL and the
// backend measures the edges from its own market read. Mirrors the backend
// CoPilotExpectedRangeModel field for field.
export interface CoPilotExpectedRangeModelDto {
    model: string;                    // "ATM Straddle" | "Custom Percentage" — the literals ARE the contract
    customPercent: number | null;     // a MULTIPLIER of the straddle, not a percent of the underlying
}

export interface CoPilotMaxRiskCeilingDto {
    kind: string;                 // "dollar" | "relative"
    amount: number | null;        // dollar: PLAIN DOLLARS, always (2026-08-13) — converted before send
    level: string | null;         // "Low" | "Moderate" | "Aggressive"
}

// The DORMANT cloud seam's Select shape (nothing sends it since the two-copilots reversal,
// CR-20260804-001 ruling AF: the Select tab fires the Select Copilot's own operation,
// RunOpgCoPilotSkill). Kept because the wire op keeps its select slot for the cloud contract;
// the engine-path fields the Select increment added (calendar strings, defaultQuantity, the
// engine directive) left with the backend's Select path.
export interface CoPilotSelectInputsDto {
    dateKind: string;             // "single" | "range"
    date: string | null;          // calendar date "yyyy-MM-dd" (single)
    rangeStart: string | null;    // range
    rangeEnd: string | null;      // range
    priceRange: CoPilotPriceRangeDto;
    minProfit: number;
    minProfitNormalized: boolean;     // the minProfit number is a normalized value, not plain dollars (v2)
    maxRisk: CoPilotMaxRiskCeilingDto;
}

export interface CoPilotAdjustInputsDto {
    priceRange: CoPilotPriceRangeDto;
    // PLAIN DOLLARS, always (2026-08-13). Renamed from `minProfit` deliberately: the old name could
    // carry either dollars or lot-normalized points depending on a companion flag, and a panel that
    // still sent the old meaning into a field read as dollars would understate the request by a
    // factor of a thousand, silently. A rename turns that mismatch into an outright refusal.
    minProfitDollars: number;
    maxRisk: CoPilotMaxRiskCeilingDto;
    // Added by CR-20260724-001; already present on the backend DTO.
    harvest: boolean;                 // cap every permitted peak at the minimum (a command, not a hint)
    // Added by CR-20260818-001 iteration 3 (additive; absent = "both" + false = the behaviour before
    // the controls existed). Both are trader COMMANDS, like harvest above.
    riskSide: string;                 // "up" | "down" | "both" | "auto" — which edge(s) may be sold beyond
    halfRisk: boolean;                // take half the 1:1 ceiling instead of all of it
    // Added by CR-20260820-002 (additive; absent = "ATM Straddle" = scale 1). Sent ONLY on an
    // "expectedRange" run — every other form has no band for it to scale, and a field nothing reads
    // is a field some future client will set and expect to work.
    expectedRangeModel?: CoPilotExpectedRangeModelDto | null;
    // The backend DTO also carries `engine` and `binaryDelivery` for wire compatibility; both were
    // retired 2026-08-08 (one composer set, clean delivery only) and the panel never sends them,
    // so they are not declared here.
}

export interface CoPilotCloseInputsDto {
    dates: string[];              // calendar dates "yyyy-MM-dd"
    targetKind: string;           // "all" | "hedges"
    hedgeIds: string[];
}

// ---- Commands ------------------------------------------------------------------------

// Run one questionnaire submission. ASYNC-fire: the WCF call returns an ack immediately while the
// ~187s skill call runs off-thread; the result arrives as CoPilotSolutionsReadyDto over ESS,
// correlated by runId (ADR-030). Exactly one of select/adjust/close is non-null (per tabKind).
// CR-20260924-002 (ES mode). Mirrors the backend EsContractCandidateDto / EsContractGapDto: one ES
// contract the lens may translate onto (nearest first; gap = the silo's fair-value offset, null =
// the backend reads the day's fair value), and on the push the gap it used per contract.
export interface EsContractCandidateDto {
    contract: string;
    gap: number | null;
}

export interface EsContractGapDto {
    contract: string;
    gap: number;
}

export class RunCoPilotSkill implements ProtocolCommand {
    constructor(
        public readonly contractVersion: number,
        public readonly runId: string,
        public readonly portfolioId: string,
        public readonly tabKind: string,
        public readonly positions: CoPilotPositionDto[],
        public readonly select: CoPilotSelectInputsDto | null,
        public readonly adjust: CoPilotAdjustInputsDto | null,
        public readonly close: CoPilotCloseInputsDto | null,
        // CR-20260924-002 (ES mode, Adjust tab), APPENDED: the positions then arrive on the ES
        // contract (the ES hedges only) and the backend lens restates them in SPX terms for the
        // engine and takes the answer back. esBase = the portfolio's asset. Absent = today's run.
        public readonly esMode: boolean = false,
        public readonly esBase: string | null = null,
        public readonly esContracts: EsContractCandidateDto[] | null = null
    ) {}

    getOperationName(): string {
        return SHELL_PROTOCOL_OPERATIONS_NS + '.RunCoPilotSkill';
    }
}

// Cancel an in-flight Run (separate op because the transport is async — there is no HTTP request to
// abort). Idempotent server-side (unknown/finished run = no-op).
export class CancelCoPilotRun implements ProtocolCommand {
    constructor(public readonly runId: string) {}

    getOperationName(): string {
        return SHELL_PROTOCOL_OPERATIONS_NS + '.CancelCoPilotRun';
    }
}

// ---- Receive-side DTOs (BE -> FE, ESS push; camelCase as observed on the FE) ----------

// Contract revised 2026-06-19 (owner #5): Solution = id/uniqueId/displayName/short+extended desc/legs;
// Leg = quantity/ticker/tickerDisplayName/actionType. Dropped name/debitCredit/maxRisk + side/strike/
// expiry/optionType (the latter four are encoded in the ticker + action). Mirrors the backend DTOs.
export interface CoPilotLegDto {
    quantity: number;
    ticker: string;            // raw full option ticker, single software format
    tickerDisplayName: string; // human-readable ("SPX(W) Call 5000 25-Jun-26")
    actionType: string;        // human-readable: "Buy To Open" | "Sell To Close" | ...
    // CR-20260724-001 stage 7 (Attribution). The EXISTING hedge this leg trades on, as the backend
    // identified it (= Hedge Matrix HedgeData.id). Absent on a To-Open leg, which belongs to no
    // hedge yet. Until stage 7 shipped, only LOCALLY-generated close legs carried this; now the
    // backend can decide the same thing and it arrives over the wire by the same path.
    groupId?: string;
}

// A HOLDING, not a trade (CR-20260724-001 stage 7): what a hedge is left owning at one contract.
// Quantity is SIGNED here — a leg's is unsigned because its action carries the side, and a holding
// has no action, so the sign lives on the number. Do not merge these two shapes.
export interface CoPilotHoldingDto {
    quantity: number;          // SIGNED: long > 0, short < 0
    ticker: string;
    tickerDisplayName: string;
}

// One card INSIDE a package (CR-20260724-001). Content only — Preview and Apply live on the
// package, because a card is never traded on its own.
export interface CoPilotCardDto {
    title: string;             // named from the legs it shows: "SPX Call Vertical" | "... Custom"
    legs: CoPilotLegDto[];     // this card's share of the package's TRADED legs
    // ---- stage 7: a card that CHANGES a hedge the book already holds ----
    // Set => this card is not a new hedge; it is a transaction on `targetHedgeId`, and Preview puts
    // that hedge into modification instead of creating one. Absent => a new hedge, as before.
    targetHedgeId?: string;
    // What the target hedge is left HOLDING afterwards — the engine's own after-state, which its
    // book laws were checked against.
    // ⚠ NOT a signal for "closes the hedge". protobuf-net writes an empty repeated field as ABSENT,
    // so an empty outcome arrives here as undefined and is indistinguishable from "none was sent".
    // Read `closesTheHedge` instead.
    targetOutcome?: CoPilotHoldingDto[];
    // True when the card's trade leaves the target hedge holding nothing.
    closesTheHedge?: boolean;
}

export interface CoPilotSolutionDto {
    id: string;                // skill-provided id (traceability)
    uniqueId: string;          // skill-provided globally-unique id (traceability)
    displayName: string;       // card title (may collide; never an identity)
    shortDescription: string;
    extendedDescription: string;
    legs: CoPilotLegDto[];     // the package's NETTED order — what Preview places
    // ---- CR-20260724-001 (absent on the LLM path; a consumer without them renders legs flat) ----
    // INVARIANT: concatenating every card's legs reproduces `legs` exactly. A leg one block buys
    // and another sells at the same strike is on NEITHER card — it is not traded, and the held
    // quantity is left alone (owner ruling 2026-07-27).
    cards?: CoPilotCardDto[];
    totalCost?: number | null; // engine's net premium in normalized points; drives the RANKING only
    isPackage?: boolean;
    // ---- CR-20260806-001: the request-fit measures behind the "hide packages that miss the
    // request" checkbox. Pure geometry (payoff read at strikes, no quote involved), and FILTERS
    // only — nothing ranks on them and the panel still sorts by price.
    // ⚠ null/undefined MEANS "this form asks no such question". A WALL asks for a payoff that
    // stands at both ends, so both are absent there and the filter stays inert.
    // ⚠ A FOLLOW-THROUGH sends outsideMiss only (CR-20260807-001, T1/W9): its closed side promises
    // to break, which is a real question; its requested area is unbounded, which is not.
    // ⚠ DOLLARS since 2026-08-13, and RENAMED from the *Points pair so the unit change cannot be
    // read as the old one. They used to arrive in lot-normalized points, which meant nothing
    // without the portfolio's default quantity; the engine no longer knows that number.
    addedAboveRequestDollars?: number | null;   // more than asked, INSIDE the request, in dollars
    outsideMissDollars?: number | null;         // payoff left standing at the ladder's far end
}

export interface CoPilotSolutionsReadyDto {
    topic: string;          // = owning TerminalId (Guid) — ESS routing
    runId: string;          // echoes RunCoPilotSkill.runId
    outcome: string;        // "succeeded" | "empty" | "terminalFailed" | "preSendBlocked"
    failureDetail: string | null;
    // WHAT THE RUN COULD NOT ANSWER, on a run that SUCCEEDED (2026-08-20). One line per dropped
    // risk level, in the engine's own words — a "Max Risk: All" run asks for three levels and may
    // come back with two. `failureDetail` reaches the panel only when EVERY level came back empty,
    // so before this a partial run looked exactly like a run that was never asked for the third.
    // ⚠ NOT a failure: it rides on outcome 'succeeded' beside real packages and must never render
    // in the error tone. Absent/empty = the run answered everything it was asked.
    notes?: string[] | null;
    solutions: CoPilotSolutionDto[];
    // CR-20260924-002 (additive on the push): on an ES-mode Adjust run, the gap the lens used per
    // contract — the legs are then on the ES contract. Absent/null on every other run.
    esContracts?: EsContractGapDto[] | null;
}

// ==== THE HEDGE MATRIX ORION CLOSE (CR-20260910-001 milestone 2, contract H1 v0.3) ============
//
// The Close is its OWN engine on the backend — its own runner, its own run registry and its own
// delivery message — so it gets its own command and its own message type here too. Nothing below
// is shared with the Adjust wire above, and that separation is deliberate: a Close supersedes only
// an earlier Close, and a Close never displaces an Adjust.
//
// Until this shipped the Close was synthesized IN THE BROWSER and the panel never sent anything.
// Now it sends like Select and Adjust and renders the push.

// The Close command's own contract revision, independent of COPILOT_CONTRACT_VERSION above: the
// two commands version separately because they are two contracts.
export const HM_CLOSE_CONTRACT_VERSION = 1;

// The ESS message type the panel subscribes to (= the backend class name; its own MessageGuid,
// registered on the three routing sites by the CR-20260910-001 foundation pass).
export const HM_CLOSE_SOLUTIONS_READY_MESSAGE_TYPE = 'HmCloseSolutionsReadyDto';

// The close questionnaire on the wire. Field-identical to CoPilotCloseInputsDto above and STILL A
// SEPARATE TYPE, mirroring the backend: HmCloseInputs is a new model of the Close's own, and the
// dormant CoPilotCloseInputs nested in the Adjust command was deliberately left untouched rather
// than retyped, because retyping a member of a live wire contract to serve a new command breaks
// the live one for the wrong reason.
export interface HmCloseInputsDto {
    dates: string[];              // existing book dates, "yyyy-MM-dd", multi-select, no range
    targetKind: string;           // "all" | "hedges"
    hedgeIds: string[];           // the chosen hedges when targetKind is "hedges"
}

// Run the close on the backend. ASYNC-fire, as the copilots: the ack returns immediately and the
// result arrives over ESS correlated by runId.
//
// ⚠ THERE IS NO CANCEL COMMAND FOR THIS, AND NONE IS TO BE ADDED. A close ends by completing, by
// its deadline, or by being superseded — pressing Apply again replaces the running one.
export class RunHedgeMatrixCloseSkill implements ProtocolCommand {
    constructor(
        public readonly contractVersion: number,
        public readonly runId: string,
        public readonly panelInstanceId: string,
        public readonly portfolioId: string,
        public readonly hedgePositions: CoPilotPositionDto[],
        public readonly close: HmCloseInputsDto,
        public readonly defaultQty: number,
        // The PORTFOLIO's asset — the base the backend's ES norm factor is a ratio against. It
        // cannot be learnt server-side: there is no by-id portfolio reader and the portfolio record
        // carries no asset, so without this an ES hedge on a mixed book closes unnormalized.
        public readonly baseAsset: string | null
    ) {}

    getOperationName(): string {
        return SHELL_PROTOCOL_OPERATIONS_NS + '.RunHedgeMatrixCloseSkill';
    }
}

// One close leg. It carries MORE than the shared copilot leg above — the option type, strike,
// expiry, hedge label and hedge colour that the close cards and the read-only transactions view
// need and that the shared shape does not have. The backend gives the Close its own leg shape for
// exactly this reason, rather than widening the shape the Adjust delivery uses.
export interface HmCloseLegDto {
    quantity: number;
    ticker: string;
    displayName: string;
    action: string;               // "Sell To Close" (a held long) | "Buy To Close" (a held short)
    side: string;                 // "sell" | "buy"
    effect: string;               // "close"
    groupId: string;              // the owning hedge — drives Preview's modification flow
    asset: string | null;         // the leg's own underlying; an ES leg prices at the norm factor
    optionType: string | null;
    strike: number | null;
    expiry: string | null;        // "yyyy-MM-dd"
    hedgeLabel: string | null;    // the hedge's display name, no "Close" prefix
    hedgeColour: string | null;
}

// The three figures of one close. ALL-OR-NOTHING: the whole object is null while any leg of that
// solution lacks a mid, and the hedge is named in `notes`. Never zero — a zero would read as a
// free close.
export interface HmClosePricesDto {
    net: number | null;           // over the actual quantities, ES legs at the norm factor
    orderPrice: number | null;    // net / HCF
    ledgerPrice: number | null;   // net / the portfolio's default quantity
}

export interface HmCloseSolutionDto {
    targetHedgeId: string;
    title: string;                // Close "<hedge name>"
    legs: HmCloseLegDto[];
    prices: HmClosePricesDto | null;
}

// Close All: every solution's legs together, with the SUMMED figures.
//
// ⚠ ITS FIGURES ARE THE SUM OF THE PER-HEDGE FIGURES, never a recomputation over the joined legs,
// and `prices` is null until every hedge is priced. The two differ — the joined legs reduce on a
// common factor across hedges that no single card uses — so the total is built from the cards the
// trader can see.
export interface HmCloseAllDto {
    legs: HmCloseLegDto[];
    prices: HmClosePricesDto | null;
}

export interface HmCloseSolutionsReadyDto {
    topic: string;                // = owning TerminalId — ESS routing
    runId: string;                // echoes RunHedgeMatrixCloseSkill.runId
    outcome: string;              // "succeeded" | "empty" | "superseded" | "terminalFailed" | "preSendBlocked"
    failureDetail: string | null; // a refusal name on preSendBlocked
    solutions: HmCloseSolutionDto[];
    closeAll: HmCloseAllDto | null;
    // "UnpricedHedge:<id>" per solution the market could not price. Not a failure — it rides on a
    // succeeded run beside priced cards.
    notes?: string[] | null;
    // NO TIME STAMP, deliberately: the cards re-price live and show no snapshot moment.
}
